import { SensorType } from '../../types/sensor-type';
import { BaseSensorWebView } from '../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import _ from 'lodash';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';

export class IntuneSensorView extends BaseSensorWebView {
  public formGroup: FormGroup;

  public dom: {
    $applicationID: GzFormFieldWebComponent;
    $tenantID: GzFormFieldWebComponent;
    $clientSecret: GzFormFieldWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
        <div class="sensor-requirement" sensor-type="${SensorType.INTUNE}">
          <gz-title class="sensor-title">${dependencies.translateFn(
            'Before you integrate Microsoft Intune with GravityZone, make sure the following requirements are met:'
          )}
          </gz-title>
          <ol>
            <li>${dependencies.translateFn('Your managed application is {registerLink} in Microsoft Azure AD.', {
              registerLink:
                '<gz-link type="underline" href="https://docs.microsoft.com/en-us/azure/active-directory/reports-monitoring/howto-configure-prerequisites-for-reporting-api#register-an-application"><span>' +
                dependencies.translateFn('registered') +
                '</span></gz-link>',
            })}</li>
            <li>${dependencies.translateFn(
              'The following {MicrosoftGraph} {permission} is configured: DeviceManagementApps.Read.All.',
              {
                MicrosoftGraph: '<b>' + dependencies.translateFn('Microsoft Graph') + '</b>',
                permission:
                  '<gz-link type="underline" href="https://docs.microsoft.com/en-us/azure/active-directory/reports-monitoring/howto-configure-prerequisites-for-reporting-api#grant-permissions"><span>' +
                  dependencies.translateFn('Application permission') +
                  '</span></gz-link>',
              }
            )}
            <p>${dependencies.translateFn(
              'Note: DeviceManagementApps.Read.All requires an Azure AD Premium P1 license.'
            )}</p>
            </li>

            <li>${dependencies.translateFn('Admin consent is granted.')}</li>
            <li>${dependencies.translateFn('{clientSecret} is generated unless you have one already.', {
              clientSecret:
                '<gz-link type="underline" href="https://docs.microsoft.com/en-us/azure/active-directory/reports-monitoring/howto-configure-prerequisites-for-reporting-api#get-your-applications-client-secret"><span>' +
                dependencies.translateFn('Client secret') +
                '</span></gz-link>',
            })}</ol>
          <p>${dependencies.translateFn(
            'For a complete Microsoft Intune sensor integration guide, refer to {detailLink}.',
            {
              detailLink:
                '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77211-452348-the-microsoft-intune-sensor.html"><span>' +
                dependencies.translateFn('this KB article') +
                '</span></gz-link>',
            }
          )}</p>
        </div>
        `;
  }

  protected createFormGroup() {
    this.dom.$applicationID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ApplicationId.MaxLength,
        this.dependencies.translateFn('The application ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ApplicationId.MaxLength,
        })
      ),
    ];
    this.dom.$tenantID.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.TenantId.MaxLength,
        this.dependencies.translateFn('The tenant ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.TenantId.MaxLength,
        })
      ),
    ];

    this.dom.$clientSecret.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ClientSecret.MaxLength,
        this.dependencies.translateFn('The Client Secret must have maximum {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ClientSecret.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Client Secret cannot contain spaces')),
    ];

    this.formGroup = new FormGroup({
      application_id: this.dom.$applicationID,
      tenant_id: this.dom.$tenantID,
      client_secret: this.dom.$clientSecret,
    });
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $applicationID: this.shadow.querySelector('#intune_application_id'),
      $tenantID: this.shadow.querySelector('#intune_tenant_id'),
      $clientSecret: this.shadow.querySelector('#intune_client_secret'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  setTranslatedStrings(): void {
    this.dom.$applicationID.label = this.dependencies.translateFn('Application ID');
    this.dom.$tenantID.label = this.dependencies.translateFn('Tenant ID');
    this.dom.$clientSecret.label = this.dependencies.translateFn('Client Secret');
    this.setBaseTranslatedStrings(SensorType.INTUNE);
  }

  protected async setViewData(viewData) {
    super.setViewData(viewData);
    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        application_id: _.get(viewData, ['response', 'configuration', 'application_id'], ''),
        tenant_id: _.get(viewData, ['response', 'configuration', 'tenant_id'], ''),
        client_secret: _.get(viewData, ['response', 'configuration', 'client_secret'], ''),
      };
    }
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }
}
